import { useId, useState } from "react";
import { DEFAULT_HOME_LOOK, type HomeLook, type HomeStageId } from "@townsy/contracts";
import type { LayoutOutcome } from "../../homes/commands";
import { FLOOR_OPTIONS, INTERIOR_WALL_OPTIONS, ROOF_COLOR_OPTIONS, STYLE_OPTIONS, WALL_COLOR_OPTIONS, lookEquals } from "../../homes/lookPalette";
import { HomeExteriorPreview, HomeInteriorPreview } from "./HomePreview";

type LookField = keyof HomeLook;

/** The fields each editor owns: Home Builders paints the outside, Furnishings the inside. */
export const LOOK_FIELDS: Record<"exterior" | "interior", LookField[]> = {
  exterior: ["style", "wallColor", "roofColor"],
  interior: ["interiorWallColor", "floor"],
};

/**
 * Apply only this editor's fields on top of the latest saved look, so saving the paint never
 * overwrites an inside change made elsewhere (and vice versa).
 */
export function mergeLookPart(saved: HomeLook, draft: HomeLook, part: "exterior" | "interior"): HomeLook {
  const next: HomeLook = { ...saved };
  for (const f of LOOK_FIELDS[part]) (next as Record<LookField, string>)[f] = draft[f];
  return next;
}

function SwatchGroup<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
  testId,
}: {
  legend: string;
  name: string;
  value: T;
  options: ReadonlyArray<{ id: T; label: string; hex?: string; description?: string }>;
  onChange(v: T): void;
  testId: string;
}) {
  const current = options.find((o) => o.id === value);
  return (
    <fieldset className="choice-group" data-movement-block="true">
      <legend>
        {legend}: <span className="muted">{current?.label ?? value}</span>
      </legend>
      <div className="choice-options">
        {options.map((opt) => (
          <label key={opt.id} className={opt.hex ? "choice choice-swatch" : "choice choice-pill"} title={opt.description ? `${opt.label}: ${opt.description}` : opt.label}>
            <input
              type="radio"
              className="choice-input"
              name={name}
              value={opt.id}
              checked={value === opt.id}
              onChange={() => onChange(opt.id)}
              data-testid={`${testId}-${opt.id}`}
            />
            {opt.hex ? (
              <>
                <span className="choice-dot" style={{ background: opt.hex }} aria-hidden="true" />
                <span className="visually-hidden">{opt.label}</span>
              </>
            ) : (
              <span className="choice-text">{opt.label}</span>
            )}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Free look editor (ADR-0005 "Free look"; docs/03 section 6 "basic appearance choices are free").
 * Visitors can try every choice in the live 2D preview; saving is for the owner and sends the
 * layout version it was based on, so a change made elsewhere is reported instead of overwritten.
 */
export function LookEditor({
  part,
  look,
  stage,
  saveBlockedReason,
  onSave,
}: {
  part: "exterior" | "interior";
  /** The saved look (default look when there is no home). */
  look: HomeLook | null;
  stage: HomeStageId | null;
  /** Why saving is impossible (guest, no home…); null for the owner. */
  saveBlockedReason: string | null;
  onSave(look: HomeLook): Promise<LayoutOutcome>;
}) {
  const uid = useId();
  const saved = look ?? DEFAULT_HOME_LOOK;
  const [draft, setDraft] = useState<HomeLook | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "warning" | "danger"; text: string } | null>(null);
  const shown = draft ?? saved;
  const dirty = draft !== null && !lookEquals(mergeLookPart(saved, draft, part), saved);
  const prefix = part === "exterior" ? "look" : "inside";

  const set = <K extends LookField>(field: K, value: HomeLook[K]) => {
    setDraft({ ...shown, [field]: value });
    setMessage(null);
  };

  const save = async () => {
    if (!draft || busy || saveBlockedReason) return;
    setBusy(true);
    setMessage(null);
    const outcome = await onSave(mergeLookPart(saved, draft, part));
    setBusy(false);
    if (outcome.ok) {
      setDraft(null);
      setMessage({ tone: "success", text: part === "exterior" ? "Saved. Everyone in town sees the new look." : "Saved. Visitors inside see the new look." });
      return;
    }
    // Keep the visitor's choices: after a conflict the saved look was reloaded and Save applies them on top.
    setMessage({ tone: outcome.conflict ? "warning" : "danger", text: outcome.message });
  };

  return (
    <div className="look-editor" data-testid={`${prefix}-editor`}>
      <div className="look-editor-preview">
        {part === "exterior" ? <HomeExteriorPreview stage={stage ?? "small_house"} look={shown} /> : <HomeInteriorPreview look={shown} />}
        <span className="hint">{dirty ? "Preview — not saved yet" : "Preview"}</span>
      </div>
      <div className="stack" style={{ gap: 10, minWidth: 0 }}>
        {part === "exterior" ? (
          <>
            <SwatchGroup legend="Style" name={`${uid}-style`} value={shown.style} options={STYLE_OPTIONS} onChange={(v) => set("style", v)} testId={`${prefix}-style`} />
            <SwatchGroup legend="Walls" name={`${uid}-wall`} value={shown.wallColor} options={WALL_COLOR_OPTIONS} onChange={(v) => set("wallColor", v)} testId={`${prefix}-wall`} />
            <SwatchGroup legend="Roof" name={`${uid}-roof`} value={shown.roofColor} options={ROOF_COLOR_OPTIONS} onChange={(v) => set("roofColor", v)} testId={`${prefix}-roof`} />
          </>
        ) : (
          <>
            <SwatchGroup
              legend="Inside walls"
              name={`${uid}-iwall`}
              value={shown.interiorWallColor}
              options={INTERIOR_WALL_OPTIONS}
              onChange={(v) => set("interiorWallColor", v)}
              testId={`${prefix}-wall`}
            />
            <SwatchGroup legend="Floor" name={`${uid}-floor`} value={shown.floor} options={FLOOR_OPTIONS} onChange={(v) => set("floor", v)} testId={`${prefix}-floor`} />
          </>
        )}
        <p className="hint" style={{ margin: 0 }}>
          Free: basic appearance choices never cost coins.
        </p>
        <div className="row">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!dirty || busy || Boolean(saveBlockedReason)}
            aria-describedby={saveBlockedReason ? `${uid}-blocked` : undefined}
            onClick={() => void save()}
            data-testid={`${prefix}-save`}
          >
            {busy ? "Saving…" : "Save look"}
          </button>
          {draft ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => {
                setDraft(null);
                setMessage(null);
              }}
            >
              Undo changes
            </button>
          ) : null}
        </div>
        {saveBlockedReason ? (
          <span id={`${uid}-blocked`} className="hint" data-testid={`${prefix}-blocked`}>
            {saveBlockedReason}
          </span>
        ) : null}
        {message ? (
          <div
            className={`notice small ${message.tone === "success" ? "notice-info" : message.tone === "warning" ? "notice-warning" : "notice-danger"}`}
            role={message.tone === "success" ? "status" : "alert"}
            data-testid={`${prefix}-message`}
          >
            {message.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}
